import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { friendlyError } from "@/lib/errors";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { Field, Input, Toggle } from "@/components/ui/Form";
import { Modal } from "@/components/ui/Modal";
import { StatusPill } from "@/components/ui/StatusPill";
import { toast } from "@/components/ui/toast";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState } from "@/components/ui/states";
import {
  createBrand,
  deleteBrand,
  fetchBrands,
  slugify,
  updateBrand,
  type BrandDraft,
  type BrandRow,
} from "./taxonomyApi";

/** Las marcas no tienen jerarquia: son una lista plana y un filtro mas. */
export function BrandsPage() {
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const [editando, setEditando] = useState<BrandRow | "nueva" | null>(null);
  const [borrando, setBorrando] = useState<BrandRow | null>(null);
  const [quitando, setQuitando] = useState(false);

  const lista = useQuery({
    queryKey: ["brands", org?.organizationId],
    queryFn: () => fetchBrands(org!.organizationId),
    enabled: Boolean(org),
  });

  const marcas = lista.data ?? [];

  async function confirmarBorrado() {
    if (!borrando) return;
    setQuitando(true);
    try {
      await deleteBrand(borrando.id);
      toast.ok("Marca eliminada");
      await qc.invalidateQueries({ queryKey: ["brands", org?.organizationId] });
      await qc.invalidateQueries({ queryKey: ["taxonomy", org?.organizationId] });
      await qc.invalidateQueries({ queryKey: ["products"] });
      setBorrando(null);
    } catch (e) {
      toast.error(friendlyError(e));
    }
    setQuitando(false);
  }

  return (
    <div className="flex flex-col gap-3">
      <Panel
        title="Marcas"
        bleed
        actions={
          <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setEditando("nueva")}>
            Nueva
          </Button>
        }
      >
        <QueryBoundary
          query={lista}
          isEmpty={(d) => d.length === 0}
          empty={
            <EmptyState
              title="Todavía no hay marcas"
              description="Sirven para filtrar el catálogo por fabricante."
            />
          }
        >
          {() => (
            <ul className="divide-y divide-line-2">
              {marcas.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5">
                  <div className="min-w-0 flex-1">
                    <span className="text-ui font-medium">{m.name}</span>
                    <span className="tnum ml-2 text-xs text-ink-3">/{m.slug}</span>
                  </div>
                  <span className="tnum text-xs text-ink-3">
                    {m.productos} producto{m.productos === 1 ? "" : "s"}
                  </span>
                  {!m.active && <StatusPill tone="neutral">Oculta</StatusPill>}
                  <Button size="sm" variant="secondary" onClick={() => setEditando(m)}>
                    Editar
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setBorrando(m)}>
                    Eliminar
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </QueryBoundary>
      </Panel>

      {editando && (
        <BrandDialog
          marca={editando === "nueva" ? null : editando}
          organizationId={org!.organizationId}
          onClose={() => setEditando(null)}
          onSaved={async () => {
            await qc.invalidateQueries({ queryKey: ["brands", org?.organizationId] });
            await qc.invalidateQueries({ queryKey: ["taxonomy", org?.organizationId] });
            setEditando(null);
          }}
        />
      )}

      <Modal
        open={Boolean(borrando)}
        onClose={() => setBorrando(null)}
        tone="danger"
        title={`Eliminar ${borrando?.name ?? ""}`}
        description={
          borrando && borrando.productos > 0
            ? `${borrando.productos} producto${borrando.productos === 1 ? "" : "s"} se quedará${borrando.productos === 1 ? "" : "n"} sin marca. Los productos no se borran.`
            : "No la usa ningún producto."
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setBorrando(null)} disabled={quitando}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => void confirmarBorrado()} loading={quitando}>
              Eliminar
            </Button>
          </>
        }
      />
    </div>
  );
}

function BrandDialog({
  marca,
  organizationId,
  onClose,
  onSaved,
}: {
  marca: BrandRow | null;
  organizationId: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [draft, setDraft] = useState<BrandDraft>({
    name: marca?.name ?? "",
    slug: marca?.slug ?? "",
    active: marca?.active ?? true,
    sort_order: marca?.sort_order ?? 0,
  });
  const [slugTocado, setSlugTocado] = useState(Boolean(marca));
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!slugTocado) setDraft((d) => ({ ...d, slug: slugify(d.name) }));
  }, [draft.name, slugTocado]);

  const error = !draft.name.trim() ? "Ponle un nombre." : !draft.slug ? "Falta el enlace." : null;

  async function guardar() {
    if (error) return;
    setGuardando(true);
    try {
      if (marca) await updateBrand(marca.id, draft);
      else await createBrand(organizationId, draft);
      toast.ok(marca ? "Marca guardada" : "Marca creada");
      await onSaved();
    } catch (e) {
      toast.error(friendlyError(e));
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={marca ? `Editar ${marca.name}` : "Nueva marca"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={() => void guardar()} loading={guardando} disabled={Boolean(error)}>
            {marca ? "Guardar" : "Crear"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="Nombre" required>
          <Input
            autoFocus
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </Field>

        <Field label="Enlace" required hint="Único en tu tienda.">
          <Input
            value={draft.slug}
            onChange={(e) => {
              setSlugTocado(true);
              setDraft({ ...draft, slug: slugify(e.target.value) });
            }}
          />
        </Field>

        <Field label="Orden" hint="Menor aparece antes. Empate: alfabético.">
          <Input
            inputMode="numeric"
            value={String(draft.sort_order)}
            onChange={(e) =>
              setDraft({ ...draft, sort_order: Number(e.target.value.replace(/\D/g, "")) || 0 })
            }
          />
        </Field>

        <Toggle
          label="Visible en la tienda"
          hint="Oculta no aparece en los filtros."
          checked={draft.active}
          onChange={(v) => setDraft({ ...draft, active: v })}
        />
      </div>
    </Modal>
  );
}
